import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Button, FormGroup, Label } from "reactstrap";
import { toast } from "react-toastify";
import { useLoader } from "../../../_helper/LoaderContext";
import excelDocumentImg from "../../../assets/images/excel-file.png";
import { importPharmaDoctorsAPI } from "../PharmaDoctorService";

const ALLOWED_TYPES = [
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.ms-excel",
];

const ImportDoctorsModal = ({ show, onHide, onImported }) => {
	const { t } = useTranslation();
	const { setLoading } = useLoader();
	const [selectedFile, setSelectedFile] = useState(null);
	const [fileName, setFileName] = useState("");
	const [fileError, setFileError] = useState("");

	const resetForm = () => {
		setSelectedFile(null);
		setFileName("");
		setFileError("");
	};

	const handleClose = () => {
		resetForm();
		onHide();
	};

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const isValidType =
			ALLOWED_TYPES.includes(file.type) ||
			file.name.toLowerCase().endsWith(".xlsx") ||
			file.name.toLowerCase().endsWith(".xls");

		if (!isValidType) {
			setFileError(t("Only Excel files (.xls, .xlsx) are allowed."));
			setSelectedFile(null);
			setFileName("");
			e.target.value = "";
			return;
		}

		setFileError("");
		setSelectedFile(file);
		setFileName(file.name);
	};

	const handleSubmit = async () => {
		if (!selectedFile) {
			setFileError(t("File is required."));
			return;
		}

		try {
			setLoading(true);
			const formData = new FormData();
			formData.append("file", selectedFile);

			const response = await importPharmaDoctorsAPI(formData);
			toast.success(
				response?.data?.message || t("Doctors imported successfully")
			);
			handleClose();
			onImported?.();
		} catch (error) {
			console.error("Error importing doctors:", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			show={show}
			onHide={handleClose}
			centered
			backdrop="static"
			keyboard={false}
			className="pharma-doctor-modal"
		>
			<Modal.Header closeButton>
				<Modal.Title>{t("Import Doctors")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<p className="small mb-3">
					{t("Upload an Excel (.xls, .xlsx) file to import doctors in bulk. 'Name' is required; other fields are optional. Use the provided template and correct headers.")}
					<br />
					<span className="text-info">
						{t("Download the sample template with export link. Check your data before uploading.")}
					</span>
				</p>
				<FormGroup>
					<Label className="col-form-label">{t("Upload File")}</Label>
					<div className="custom-file-upload">
						<label className="file-label form-control">
							<input
								type="file"
								className="file-input"
								onChange={handleFileChange}
								style={{ display: "none" }}
								accept=".xlsx,.xls"
							/>
							<div className="file-name-container">
								<span className="wrap-file-name">
									{fileName || t("No file chosen")}
								</span>
							</div>
							<span className="upload-btn">{t("Choose File")}</span>
						</label>
					</div>
					{fileError && <div className="text-danger mt-1">{fileError}</div>}
				</FormGroup>

				{selectedFile && (
					<div className="mt-3">
						<img
							src={excelDocumentImg}
							style={{
								objectFit: "fill",
								borderRadius: "10px",
							}}
							height="65"
							width="65"
							alt={t("Excel file")}
						/>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button color="primary" onClick={handleSubmit}>
					{t("Import")}
				</Button>
				<Button color="secondary" onClick={handleClose}>
					{t("Cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default ImportDoctorsModal;
